<template>
  <div v-if="showProgressInfo || isSetup" :class="wrapperClasses" class="eds-Wrapper">
    <slot name="content">
      <template v-if="showProgressInfo">
        <div :class="progressInfoClasses" class="eds-ProgressInfo">
          <div class="eds-ProgressInfo_Content">
            <AppIcon
              v-if="!dataSourceQuery.isValid || dataSourceQuery.errorMessage"
              v-tippy="{
                content: t('data_source.sync_message.error'),
                placement: 'top'
              }"
              class="eds-WarningIcon"
              height="24"
              icon-name="info-error"
              width="24"
            />
            <template v-else>
              <ElementDataSourceIcon :type-id="dataSourceQuery.dataSourceTypeId" />
            </template>
            <span class="eds-QueryName oboard-truncated-text" @click="onOpenDataSourceQuery">
              {{ dataSourceQuery.dataSourceQueryName }}
            </span>
          </div>

          <div class="eds-ProgressInfo_Action">
            <div v-if="dataSourceQuery.lastSyncDate" class="eds-ProgressInfo_TimeAgo">
              {{ getTimeAgo(dataSourceQuery.lastSyncDate) }}
            </div>
            <AppButtonNext
              v-if="isSetup && userCanUpdateObjective"
              :icon-density="BUTTON_ICON_DENSITIES.FIT"
              :size="BUTTON_SIZES_NEXT.XS"
              :variant="BUTTON_VARIANTS.LINK"
              icon-before="unlink-next"
              @click="onUnlinkDataSource"
            />
          </div>
        </div>
      </template>
      <template v-else-if="isSetup">
        <div
          :id="`dsa-Trigger-${uniqId}`"
          :class="{ 'eds-AddDataSource_Binary': isBinary }"
          class="eds-AddDataSource"
        >
          <div class="eds-AddDataSource_Title">
            <AppIcon height="24" icon-name="link" width="24" />
            {{ $t('data_source.title') }}
          </div>

          <AppIcon height="24" icon-name="plus-next" width="24" />
        </div>

        <DropdownMenu
          :items="options"
          :offset="[0, 0]"
          :show="showDropdown"
          :to-selector="`#dsa-Trigger-${uniqId}`"
          :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
          follow-cursor="initial"
          preferred-position="bottom-end"
          @update:show="showDropdown = $event"
          @item-click="onOptionClick"
        />
      </template>
    </slot>

    <portal to="modal-windows">
      <PowerBIQueryModal
        v-model:show="powerBIOpen"
        :data-source-query="powerBIDataSourceQuery"
        :element-id="element[OKR_ELEMENT_ENTITY_KEYS.ID]"
        @add-data-source="onAddDataSource"
        @update-element="onUpdateElement"
      />
      <AppDeleteModal
        :loading="deleteDataSourceQueryLoading"
        :show="deleteDataSourceQueryOpen"
        :title="$t('data_source.power_bi.delete_query')"
        keypath="data_source.power_bi.delete_query.message"
        @on-confirm="onConfirmDeleteDataSourceQuery"
        @on-close="onCloseDeleteDataSourceQuery"
      />
    </portal>
  </div>
</template>

<script setup>
import { isEmpty, isNil } from 'lodash'
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import DataSourcesApiHandler from '@/api/datasources'
import { useMsAuth } from '@/composables/ms-auth'
import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES_NEXT,
  BUTTON_VARIANTS
} from '@/utils/components-configurations/app-button'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import {
  DATA_SOURCE_MENU_ACTIONS,
  DATA_SOURCE_MENU_GROUPS,
  DATA_SOURCE_TYPES,
  DATA_SOURCE_INTEGRATION_HELP_LINK
} from '@/utils/datasources'
import { MENU_ITEMS_GROUPS } from '@/utils/dropdown-menu'
import { ELEMENT_PROGRESS_ADJUSTMENT_TABS } from '@/utils/element-types'
import { OKR_ELEMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import { openLink } from '@/utils/external-link-helper'
import { currentUserCanUpdateObjective } from '@/utils/objectives'
import { isBinaryProgress, isThresholdProgress } from '@/utils/okr-element-values'
import { includesValue, mapValuesByKey } from '@/utils/prop-validators'
import { getTimeAgo } from '@/utils/time-ago'

import ElementDataSourceIcon from '@/components/form/ElementDataSourceIcon'
import PowerBIQueryModal from '@/components/form/PowerBIQueryModal'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'

defineOptions({
  name: 'ElementDataSource'
})

const props = defineProps({
  element: {
    type: Object,
    required: true
  },

  type: {
    type: String,
    validator: v => includesValue(v, mapValuesByKey(ELEMENT_PROGRESS_ADJUSTMENT_TABS, 'value')),
    default: ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP.value
  }
})

const emit = defineEmits({
  'update-element': null
})

const { t } = useI18n()
const { connectPowerBI } = useMsAuth()
const store = useStore()

const dataSourceQuery = computed(() => props.element.dataSourceQuery ?? {})
const userDataSources = computed(() => store.state.system.userData?.userDataSources ?? [])

const isBinary = computed(() => {
  return isBinaryProgress({ element: props.element })
})

const isSetup = computed(() => {
  return props.type === ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP.value
})

const isChart = computed(() => {
  return props.type === ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.value
})

const userCanUpdateObjective = computed(() => {
  return currentUserCanUpdateObjective(props.element)
})

const showProgressInfo = computed(() => {
  return (
    dataSourceQuery.value && dataSourceQuery.value.dataSourceId && (isSetup.value || isChart.value)
  )
})

const wrapperClasses = computed(() => {
  return {
    'eds-Wrapper-progress': isChart.value && !isNil(dataSourceQuery.value.dataSourceId),
    'eds-Wrapper-binary': isBinary.value,
    'eds-Wrapper-progress-threshold':
      isThresholdProgress({ element: props.element }) && isChart.value
  }
})

const progressInfoClasses = computed(() => {
  return {
    'eds-ProgressInfo-query': isSetup.value,
    'eds-ProgressInfo-queryBinary': isSetup.value && isBinary.value
  }
})

//#region Dropdown
const showDropdown = ref(false)
const uniqId = useId()

const options = computed(() => {
  return [
    {
      name: DATA_SOURCE_MENU_ACTIONS.POWER_BI,
      title: 'action.power_bi',
      icon: 'power-bi',
      group: DATA_SOURCE_MENU_GROUPS.USER_DATA_SOURCES
    },
    {
      name: DATA_SOURCE_MENU_ACTIONS.GOOGLE_SHEETS,
      title: 'action.google_sheets',
      icon: 'google-sheets',
      tooltipContent: t('data_source.google_sheets.coming_soon'),
      disabled: true,
      group: DATA_SOURCE_MENU_GROUPS.USER_DATA_SOURCES
    },
    {
      name: DATA_SOURCE_MENU_ACTIONS.REQUEST_INTEGRATION,
      title: 'action.request_integration',
      icon: 'request-integration',
      group: MENU_ITEMS_GROUPS.ACTIONS,
      iconColor: 'var(--dark-2)'
    }
  ]
})

const onOptionClick = id => {
  if (id === DATA_SOURCE_MENU_ACTIONS.POWER_BI) {
    if (!isEmpty(userDataSources.value)) {
      const lastAddedDataSource = userDataSources.value.at(-1)
      openPowerBIModalWithSelectedDataSource(lastAddedDataSource.id)
    } else {
      onConnectPowerBI()
    }
  } else if (id === DATA_SOURCE_MENU_ACTIONS.REQUEST_INTEGRATION) {
    openLink({ url: DATA_SOURCE_INTEGRATION_HELP_LINK, useBrowserWorkaround: true })
  }
}
//#endregion

//#region Connect Power BI
const powerBIOpen = ref(false)
const powerBIDataSourceQuery = ref(null)

const onConnectPowerBI = ({ openModal = false } = {}) => {
  const getPowerBIDataSources = () => {
    return userDataSources.value.filter(d => d.typeId === DATA_SOURCE_TYPES.POWER_BI)
  }
  return connectPowerBI().then(async () => {
    const previousIds = new Set(getPowerBIDataSources().map(d => d.id))
    await store.dispatch('system/fetchUserDataSources')

    if (openModal) {
      const lastDataSource = getPowerBIDataSources().at(-1)
      if (lastDataSource) {
        openPowerBIModalWithSelectedDataSource(lastDataSource.id)
      }
    } else {
      const lastAddedDataSource = getPowerBIDataSources().find(d => !previousIds.has(d.id))
      if (lastAddedDataSource) {
        openPowerBIModalWithSelectedDataSource(lastAddedDataSource.id)
      }
    }
  })
}

const openPowerBIModalWithSelectedDataSource = dataSourceId => {
  powerBIDataSourceQuery.value = {
    dataSourceId
  }
  powerBIOpen.value = true
}
//#endregion

//#region Add/Update Data Source
const onAddDataSource = () => {
  onConnectPowerBI({ openModal: true })
  powerBIOpen.value = false
}

const onUpdateElement = () => {
  emit('update-element')
}
//#endregion

//#region Open Data Source Query
const onOpenDataSourceQuery = () => {
  powerBIDataSourceQuery.value = props.element.dataSourceQuery
  powerBIOpen.value = true
}
//#endregion

//#region Delete Data Source Query
const deleteDataSourceQueryOpen = ref(false)
const deleteDataSourceQueryLoading = ref(false)

const onConfirmDeleteDataSourceQuery = async () => {
  const dataSourcesApi = new DataSourcesApiHandler()

  deleteDataSourceQueryLoading.value = true
  try {
    await dataSourcesApi.unlinkQuery({
      dataSourceId: props.element.dataSourceQuery.dataSourceId,
      dataSourceQueryId: props.element.dataSourceQuery.dataSourceQueryId
    })
    emit('update-element')
    onCloseDeleteDataSourceQuery()
  } finally {
    deleteDataSourceQueryLoading.value = false
  }
}

const onCloseDeleteDataSourceQuery = () => {
  deleteDataSourceQueryOpen.value = false
}

const onUnlinkDataSource = () => {
  deleteDataSourceQueryOpen.value = true
}
//#endregion
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/result-type-settings';
.eds-Wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  user-select: none;

  &:before {
    content: '';
    position: absolute;
    left: calc(var(--padding-block, 0) * -1);
    width: calc(100% + (var(--padding-block, 0) * 2));
    top: 0;
    height: 2px;
    background-color: $grey-2-next;
  }

  &:not(&-binary) {
    margin-top: var(--padding-block, 0);
  }

  &-progress {
    padding: 0 18px 18px 18px;
  }

  &-progress-threshold {
    padding-top: 18px;
  }
}

.eds-AddDataSource {
  @extend %width-styles;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding-top: var(--padding-block, 0);
  cursor: pointer;
  color: $dark-2;

  &:hover {
    color: $primary-color-next;
  }
}
.eds-AddDataSource_Binary {
  padding: 18px var(--padding-block) 20px;
}

.eds-AddDataSource_Title {
  display: flex;
  align-items: center;
  gap: inherit;
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;
}

.eds-ProgressInfo {
  @extend %width-styles;
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: space-between;
}

.eds-ProgressInfo-queryBinary {
  padding-bottom: 18px;
  padding-left: 18px;
  padding-right: 18px;
}

.eds-ProgressInfo-query {
  padding-top: 18px;
}

.eds-ProgressInfo_Content {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.eds-WarningIcon {
  flex-shrink: 0;
}

.eds-QueryName {
  overflow: hidden;
  color: $primary-color-next;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;
  cursor: pointer;

  @media (any-hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
}

.eds-ProgressInfo_Action {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}

.eds-ProgressInfo_TimeAgo {
  overflow: hidden;
  color: $dark-2;
  text-overflow: ellipsis;

  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;

  white-space: nowrap;
}
</style>
